import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSocket } from "../../context/SocketContext";
import { useTranslation } from "../../context/TranslationContext";
import { getAllMyMintSpaces } from "../../api/mintSpace";
import { getConversations, getMessageRequests } from "../../api/messages";
import { getBalance } from "../../api/balance";
import { sortMintSpacesByRecent } from "../../utils/mintSpaceRecent";
import { belongsInInbox } from "../../utils/messageRequests";

/*
  The data the app shell shows, loaded once for the whole frame: the
  signed-in person's Mint Spaces, their balance and their inbox. Each refetches
  per account and on the window events the rest of the app already sends.
*/

export const DESKTOP_QUERY = "(min-width: 1024px)";

export function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return desktop;
}

/** Closes a popover on a click outside every ref given, or on Escape. */
export function useDismiss(refs, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const list = Array.isArray(refs) ? refs : [refs];
    const onPointer = (event) => {
      if (!list.some((ref) => ref.current && ref.current.contains(event.target))) onClose();
    };
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [refs, open, onClose]);
}

/** Owned spaces ("My businesses") and the ones joined ("Other businesses"). */
export function useMySpaces() {
  const { isAuthenticated, user } = useAuth();
  const accountId = user?.id || user?._id || null;
  const [spaces, setSpaces] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setSpaces([]);
    const load = async () => {
      if (!isAuthenticated) return;
      try {
        const result = await getAllMyMintSpaces();
        if (!cancelled && result.success && result.data) setSpaces(sortMintSpacesByRecent(result.data));
      } catch {
        if (!cancelled) setSpaces([]);
      }
    };
    load();
    const resort = () => setSpaces((prev) => sortMintSpacesByRecent(prev));
    const events = ["mintSpaceCreated", "mintSpaceJoined", "mintSpaceDeleted"];
    events.forEach((name) => window.addEventListener(name, load));
    window.addEventListener("mintSpaceInteraction", resort);
    return () => {
      cancelled = true;
      events.forEach((name) => window.removeEventListener(name, load));
      window.removeEventListener("mintSpaceInteraction", resort);
    };
  }, [isAuthenticated, accountId]);

  return {
    owned: spaces.filter((space) => space.isOwner),
    joined: spaces.filter((space) => space.isMember && !space.isOwner),
  };
}

/** Available balance plus what is on hold, refreshed on "balance-updated". */
export function useBalance() {
  const { isAuthenticated, user } = useAuth();
  const userId = user?.id ?? user?._id;
  const [balance, setBalance] = useState({ amount: 0, holdBalance: 0, loaded: false });

  const load = useCallback(() => {
    if (!isAuthenticated || !userId) {
      setBalance({ amount: 0, holdBalance: 0, loaded: false });
      return;
    }
    getBalance(userId)
      .then((data) => {
        const amount = data?.amount ?? data?.data?.amount ?? 0;
        const holdBalance = data?.holdBalance ?? data?.pendingAmount ?? data?.data?.holdBalance ?? 0;
        setBalance({ amount: Number(amount) || 0, holdBalance: Number(holdBalance) || 0, loaded: true });
      })
      .catch(() => setBalance({ amount: 0, holdBalance: 0, loaded: true }));
  }, [isAuthenticated, userId]);

  useEffect(() => {
    load();
    window.addEventListener("balance-updated", load);
    return () => window.removeEventListener("balance-updated", load);
  }, [load]);

  return balance;
}

/** Inbox conversations (newest first) and pending message requests. */
export function useInbox(setToast) {
  const { isAuthenticated, user } = useAuth();
  const { socket } = useSocket();
  const { t } = useTranslation();
  const userId = user?.id ?? user?._id;
  const [conversations, setConversations] = useState([]);
  const [requests, setRequests] = useState([]);
  const toastRef = useRef(setToast);
  toastRef.current = setToast;

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setConversations([]);
      setRequests([]);
      return;
    }
    try {
      const [conversationsResponse, requestsResponse] = await Promise.all([
        getConversations(),
        getMessageRequests("received"),
      ]);
      if (conversationsResponse.success) {
        // Requests sent to this user wait under Requests; the ones they sent stay in the inbox
        const inbox = (conversationsResponse.data || []).filter((c) => belongsInInbox(c, userId));
        const at = (c) => new Date(c.lastMessageAt || c.updatedAt);
        setConversations(inbox.sort((a, b) => at(b) - at(a)));
      }
      if (requestsResponse.success) {
        setRequests((requestsResponse.data || []).filter((request) => request.status === "pending"));
      }
    } catch (error) {
      console.error("Error fetching inbox:", error);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    window.addEventListener("messages-updated", load);
    return () => {
      clearInterval(interval);
      window.removeEventListener("messages-updated", load);
    };
  }, [load]);

  useEffect(() => {
    if (!socket || !isAuthenticated) return undefined;
    const onRequest = ({ request }) => {
      setRequests((prev) => (prev.some((r) => r._id === request._id) ? prev : [request, ...prev]));
      const sender = request.from?.username || request.from?.businessName || "Someone";
      toastRef.current?.(t("messages.newMessageRequestFrom", { name: sender }) || `New message request from ${sender}`);
    };
    // A group this user was added to, or that changed, or that they're no longer in.
    const refresh = () => window.dispatchEvent(new CustomEvent("messages-updated"));
    socket.on("new_message_request", onRequest);
    socket.on("new_message", refresh);
    socket.on("group_updated", refresh);
    socket.on("group_removed", refresh);
    return () => {
      socket.off("new_message_request", onRequest);
      socket.off("new_message", refresh);
      socket.off("group_updated", refresh);
      socket.off("group_removed", refresh);
    };
  }, [socket, isAuthenticated, t]);

  const unread = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0) + requests.length;
  return { conversations, requests, unread };
}
